import React, { useEffect, useState } from "react";
import Order from "../../models/Order";
import { capitalizeFirst, padNumber } from "../../utils/string";
import Hotel from "../../models/Hotel";
import { calculateBillCharge } from "../../utils/bill";
import BookedRoom from "../../models/BookedRoom";
import Logo from "../../assets/hotel_logo.png";

import { formatDate, } from "../../utils/date";
import { attachRoomDataToBookings } from "../../utils/booking-helper";
import { FaTimes } from "react-icons/fa";


type BillProps = {
  bookings?: BookedRoom[];
  orders?: Order[];
  discount?: number;
  hotel: Hotel;
  guestName?: string;
  issuedBy?: string;
  issuedAt?: string;
  billNumber: string;
  onClose: () => void;
};

const Bill: React.FC<BillProps> = ({
  bookings = [],
  orders = [],
  discount = 0,
  hotel,
  guestName = "",
  issuedBy = "Staff",
  issuedAt,
  billNumber,
  onClose,
}: BillProps) => {
  const [filter] = useState<"all" | "paid" | "unpaid">("all");
  const [bookingsWithRooms, setBookingsWithRooms] = useState<
    (BookedRoom & { roomName: string, roomNumber: string })[]
  >([]);

  // Filter helper
  const applyFilter = <T extends { isPaid: boolean }>(list: T[]) => {
    if (filter === "paid") return list.filter((x) => x.isPaid);
    if (filter === "unpaid") return list.filter((x) => !x.isPaid);
    return list;
  };

  const filteredBookings = applyFilter(bookings);
  const filteredOrders = applyFilter(orders);

  const allItems = [...filteredBookings, ...filteredOrders];

  // Type guard: check if item is an Order
  function isOrder(item: any): item is Order {
    return Array.isArray(item.orderItems);
  }

  const { totalAmount, totalUnpaid, discountUnPaid } = allItems.reduce(
    (acc, item) => {
      if (isOrder(item)) {
        // ✅ It's an order
        const orderTotal = (item?.orderItems ?? []).reduce(
          (sum, oi) => sum + (oi.amount || 0),
          0
        );
        acc.totalAmount += orderTotal;
        if (!item.isPaid) acc.totalUnpaid += orderTotal;
      } else {
        // ✅ It's a booking
        acc.totalAmount += item.amount || 0;
        if (!item.isPaid) {
          acc.totalUnpaid += item.amount;
          acc.discountUnPaid += item.discount;
        }
      }

      return acc;
    },
    { totalAmount: 0, totalUnpaid: 0, discountUnPaid: 0 }
  );

  const [charges, setCharges] = useState({
    vatCharge: 0,
    serviceCharge: 0,
    subTotal: 0,
    total: 0,
    vatRate: 0,
    serviceChargeRate: 0,
  });

  useEffect(() => {
    const run = async () => {
      const res = await calculateBillCharge(totalAmount, discount);
      setCharges(res);
    };
    run();
  }, [totalAmount]);

  useEffect(() => {
    const fetchRooms = async () => {
      const results = await attachRoomDataToBookings(filteredBookings);
      setBookingsWithRooms(results);
    };

    if (filteredBookings.length > 0) {
      fetchRooms();
    } else {
      setBookingsWithRooms([]);
    }
  }, [filteredBookings]);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50 
                 print:bg-transparent print:static print:block"
    >
      <div
        className="relative bg-white w-[650px] max-w-full rounded-lg shadow-lg p-6 
                   overflow-y-auto max-h-[85vh]
                   printable-container 
                   print:max-h-none print:shadow-none print:rounded-none 
                   print:w-[800px] print:mx-auto print:p-0"
      >

        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-600 hover:text-red-600 text-2xl font-bold no-print z-50"
          aria-label="Close"
        >
          {FaTimes({})}
        </button>


        {/* Print Styles */}
        <style>
          {`
    @media print {
      html, body {
        height: auto !important;
        overflow: visible !important;
        background: white !important;
      }
      body {
        margin: 0 !important;
        padding: 0 !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
        font-family: Arial, sans-serif;
      }
      /* Hide UI controls */
      .no-print, 
      .no-print * {
        display: none !important;
      }
      /* Make modal full width/height for clean printing */
      .printable-container {
        position: static !important;
        width: 100% !important;
        max-width: 100% !important;
        height: auto !important;
        max-height: none !important;
        box-shadow: none !important;
        border-radius: 0 !important;
        overflow: visible !important;
        padding: 0 !important;
      }
      /* Clean tables */
      table {
        width: 100%;
        border-collapse: collapse;
        page-break-inside: avoid;
      }
      th, td {
        border: 1px solid #000 !important;
        padding: 6px 8px !important;
        text-align: center !important;
      }
      thead {
        background: #f3f3f3 !important;
        -webkit-print-color-adjust: exact;
      }
      /* Remove gray background from overlay */
      .bg-black, .bg-opacity-40 {
        background: transparent !important;
      }
    }
  `}
        </style>


        {/* Hotel Header */}
        <div className="text-center border-b pb-4 mb-4">

          <img
            src={Logo}
            alt="hotel logo"
            className="mx-auto w-20 h-20 object-contain mb-2"
          />

          <h1 className="text-2xl font-bold">{hotel?.name}</h1>
          <p className="text-sm text-gray-600">{hotel?.address}</p>

          <p className="text-sm">
            Phone: {hotel?.phone}
            {hotel?.altPhone && <> | Alt: {hotel.altPhone}</>}
          </p>

          {hotel?.email && (
            <a
              href={`mailto:${hotel.email}`}
              className="text-blue-600 text-sm underline block"
            >
              {hotel.email}
            </a>
          )}

          {hotel?.website && (
            <a
              href={hotel.website}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 text-sm underline block"
            >
              {hotel.website}
            </a>
          )}
        </div>

        {/* Bill Metadata */}
        <div className="flex justify-between text-sm text-gray-700 mb-6">
          <div>
            <p>
              <span className="font-semibold uppercase">Guest:</span> {guestName}
            </p>
            <p>
              <span className="font-semibold uppercase">Bill No:</span> {billNumber}
            </p>
          </div>
          <div className="text-right">
            <p>
              <span className="font-semibold uppercase">Issued By:</span>{" "}
              {issuedBy}
            </p>
            <p>
              <span className="font-semibold uppercase">Date:</span>{" "}
              {issuedAt || new Date().toLocaleString()}
            </p>
          </div>
        </div>

        {/* Filter Controls */}
        {/* <div className="mb-6 flex items-center justify-between no-print">
          <label className="text-sm font-medium">Show:</label>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as any)}
            className="ml-2 border rounded p-1 text-sm"
          >
            <option value="all">All</option>
            <option value="paid">Paid only</option>
            <option value="unpaid">Unpaid only</option>
          </select>
        </div> */}

        {/* Bookings */}
        {bookingsWithRooms.length > 0 && (
          <div className="mb-6">
            <h2 className="text-base font-semibold mb-2">Room Charges</h2>
            <table className="w-full border text-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="p-2 border text-center">Room</th>
                  {/* <th className="p-2 border text-center">Duration</th> */}
                  <th className="p-2 border text-center">Checkin Date</th>
                  <th className="p-2 border text-center">Checkout Date</th>
                  <th className="p-2 border text-center">Amount (₦)</th>
                  <th className="p-2 border text-center">Status</th>
                </tr>
              </thead>
              <tbody>

                {bookingsWithRooms.sort(
                  (a, b) => new Date(b.checkInDate).getTime() - new Date(a.checkInDate).getTime()
                ).map((b) => (
                  <tr key={b.id}>
                    <td className="p-2 border text-center">
                      {capitalizeFirst(b.roomName)} / {b.roomNumber.toUpperCase()}
                    </td>
                    <td className="p-2 border text-center">{formatDate(b.checkInDate as string)}</td>
                    <td className="p-2 border text-center">{formatDate(b.checkOutDate as string)}</td>
                    <td className="p-2 border text-center">
                      {b.amount.toLocaleString()}
                    </td>
                    <td
                      className={`p-2 border text-center ${b.isPaid ? "text-green-600" : "text-red-600"
                        }`}
                    >
                      {b.isPaid ? "Paid" : "Unpaid"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Orders */}
        {filteredOrders.length > 0 && (
          <div className="mb-6">
            <h2 className="text-base font-semibold mb-2">Orders</h2>
            {filteredOrders.map((o) => (
              <div key={o.orderNo} className="mb-4 border rounded">
                <div className="flex justify-between p-2 bg-gray-50 border-b">
                  {o.orderNo && <span className="font-medium">
                    Order #{padNumber(o.orderNo as number)}
                  </span>}
                  <span
                    className={o.isPaid ? "text-green-600" : "text-red-600"}
                  >
                    {o.isPaid ? "Paid" : "Unpaid"}
                  </span>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="p-2 border text-center">Item</th>
                      <th className="p-2 border text-center">Qty</th>
                      <th className="p-2 border text-center">Price (₦)</th>
                      <th className="p-2 border text-center">Total (₦)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(o.orderItems ?? []).map((it, idx) => (
                      <tr key={idx}>
                        <td className="p-2 border text-center">{it.itemName}</td>
                        <td className="p-2 border text-center">
                          {it.quantity}
                        </td>
                        <td className="p-2 border text-center">
                          {(it.amount / it.quantity).toLocaleString()}
                        </td>
                        <td className="p-2 border text-center">
                          {(it.amount).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-gray-50 font-semibold">
                      <td colSpan={3} className="p-2 border text-center">
                        Order Total
                      </td>
                      <td className="p-2 border text-center">
                        {(o?.orderItems ?? []).reduce(
                          (sum, item) => sum + item.amount, 0
                        ).toLocaleString()}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}

        {/* Totals Section — Receipt Style */}
        <div className="border-t border-dotted border-gray-300 pt-4 text-sm space-y-2 font-sans">
          {/* Subtotal */}
          <div className="flex justify-between">
            <span className="font-medium text-gray-700">Subtotal:</span>
            <span className="tabular-nums text-right text-gray-800">
              ₦{charges.subTotal.toLocaleString()}
            </span>
          </div>

          {/* Discount */}
          {discount > 0 && (
            <div className="flex justify-between text-red-600">
              <span className="font-medium">Discount:</span>
              <span className="tabular-nums text-right">- ₦{discount.toLocaleString()}</span>
            </div>
          )}

          {/* VAT */}
          {charges.vatCharge > 0 && (
            <div className="flex justify-between">
              <span className="font-medium text-gray-700">
                VAT ({charges.vatRate.toFixed(1)}%):
              </span>
              <span className="tabular-nums text-right text-gray-800">
                ₦{charges.vatCharge.toLocaleString()}
              </span>
            </div>
          )}

          {/* Service Charge */}
          {charges.serviceCharge > 0 && (
            <div className="flex justify-between">
              <span className="font-medium text-gray-700">
                Service Charge ({charges.serviceChargeRate.toFixed(0)}%):
              </span>
              <span className="tabular-nums text-right text-gray-800">
                ₦{charges.serviceCharge.toLocaleString()}
              </span>
            </div>
          )}

          {/* Grand Total */}
          <div className="flex justify-between font-semibold text-base border-t border-dotted border-gray-300 pt-3 mt-3">
            <span className="text-gray-900">Grand Total:</span>
            <span className="tabular-nums text-right text-gray-900">
              ₦{(charges.total - discount).toLocaleString()}
            </span>
          </div>

          {/* Total Paid */}
          {/* <div className="flex justify-between text-green-700 border-t border-dotted border-gray-300 pt-3 mt-3">
            <span className="font-medium">Total Paid:</span>
            <span className="tabular-nums text-right font-medium">totalUnpaid}</span>
          </div> */}

          {/* Balance */}
          {totalUnpaid - discount > 0 && totalUnpaid - discount !== charges.total - discount && (<div className="flex justify-between text-orange-600 border-t border-dotted border-gray-300 pt-3 mt-3">
            <span className="font-medium">Balance:</span>
            <span className="tabular-nums text-right font-medium">
              ₦{(totalUnpaid - discountUnPaid).toLocaleString()}
            </span>
          </div>)}
        </div>

        {/* Actions */}
        <div className="mt-6 flex justify-end gap-4 no-print">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400 transition"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              const el = document.querySelector(".printable-container") as HTMLElement;
              if (!el) return;

              const printWindow = window.open("", "_blank", "width=850,height=1000");
              if (!printWindow) return;

              const styles = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
                .map((node) => node.outerHTML)
                .join("\n");

              printWindow.document.write(`
      <html>
        <head>
          <title>Bill - ${document.title}</title>
          ${styles}
          <style>
            @media print {
              body {
                margin: 0;
                background: #fff;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              .no-print { display: none !important; }
            }
            body {
              font-family: Arial, sans-serif;
              color: #111;
              display: flex;
              justify-content: center;
              align-items: flex-start;
              padding: 40px;
            }
            .print-area {
              width: 100%;
              max-width: 750px;
              background: white;
              border: 1px solid #ddd;
              border-radius: 8px;
              padding: 30px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 10px;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: center;
            }
            th {
              background: #f3f3f3;
              font-weight: 600;
            }
            h1, h2, h3, p {
              margin: 0;
            }
            .footer {
              margin-top: 30px;
              text-align: center;
              font-size: 13px;
              border-top: 1px solid #ccc;
              padding-top: 10px;
              color: #333;
            }
          </style>
        </head>
        <body>
          <div class="print-area">
            ${el.innerHTML}
            <div class="footer">Thank you for your patronage!</div>
          </div>
        </body>
      </html>
    `);

              printWindow.document.close();

              printWindow.onload = () => {
                printWindow.focus();
                printWindow.print();
                setTimeout(() => printWindow.close(), 500);
              };
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
          >
            Print
          </button>
        </div>
      </div>
    </div>
  );
};

export default Bill;
